import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Check, ChevronDown, HelpCircle, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils";
import { usePaStructuredEnums } from "@/hooks/use-transition-retry-stats";
import type { DashboardStatsQuery, TransitionRetryStats } from "@/types";

type Props = {
  statsQuery: DashboardStatsQuery;
  refetchKey: number;
  /** Fires when enum list or path-analytics data is (re)loading. */
  onLoadingChange?: (loading: boolean) => void;
  /** Transition payload from dashboard analytics bundle (same as transition-retry-stats). */
  bundleTransition: TransitionRetryStats | null;
  bundleLoading: boolean;
  /** Notify parent when path filter selection changes (parent refetches bundle). */
  onPathFilterParamsChange: (fromStatuses: string[], toStatuses: string[]) => void;
  onBundleRefresh: () => void;
};

function formatEnumLabel(s: string): string {
  return s.replace(/_/g, " ");
}

function formatFixed2(n: number | null | undefined): string | null {
  if (typeof n !== "number" || Number.isNaN(n)) return null;
  return n.toFixed(2);
}

function formatSharePercent(n: number | null | undefined): string | null {
  if (typeof n !== "number" || Number.isNaN(n)) return null;
  return `${(n * 100).toFixed(1)}%`;
}

const PATH_STAT_HELP = {
  matching:
    "How many case paths are included: they must match the dashboard case filters, then match your From set (if any) and a later To set (if any). Leave both empty for the overview. With only From selected, “to” means the next successful PA (any status). With only To selected, “from” means any earlier failed call (any fail reason).",
  documents:
    "Raw Firestore case document rows used for this request. One “document” can be a fragment or a linked file; logical grouping may merge several rows into one case.",
  logical:
    "Distinct cases after merging documents with the same leading case id (UUID) so a single patient is not double-counted across rows.",
} as const;

type RetryStatKey = "mean" | "median" | "p90" | "p95" | "iqr" | "zero";

function helpForRetryStat(key: RetryStatKey, isFirst: boolean): string {
  const c = isFirst
    ? "In this mode (all from/to selected), the count is how many call attempts came before the first time a PA in your set was successfully obtained (0 = first call succeeded). "
    : "The count is how many calls fall strictly between the paired “from” row and the first later “to” row that matches your filters (0 = the “to” call immediately followed the “from” call). ";

  switch (key) {
    case "mean":
      return (
        c +
        "The mean is the ordinary average of that per-case value. It is easy to interpret but can be pulled up by a few long paths."
      );
    case "median":
      return (
        c +
        "The median (50th percentile) is the middle value: half the cases are at or below, half are above, using the same per-case value as the bars. It is more robust to a few very long paths than the mean."
      );
    case "p90":
      return (
        c +
        "P90: 90% of cases had this value or less; 10% were at or above. Useful to understand the heavier (but not extreme) end of the distribution."
      );
    case "p95":
      return (
        c +
        "P95: 95% of cases are at or below; about 5% sit in the thickest tail—helpful for conservative planning and worst-common-case thinking."
      );
    case "iqr":
      return `IQR is Q3 minus Q1 (the middle 50% of the per-case values), with quartiles from linearly interpolated percentiles. Large IQR = the “typical spread” of paths is wide, without being driven by a single extreme outlier. ${c}`.trim();
    case "zero":
      return isFirst
        ? c +
            "“At 0%” is the share of cases whose count is exactly zero: the first qualifying call already had a success in the PA set (no “prior” attempts before that, in the sense of this metric)."
        : c +
            "“At 0%” is the share of cases with zero between calls: the “to” row immediately followed the paired “from” row.";
  }
}

function pathStatLabel(isFirst: boolean, k: RetryStatKey): string {
  const t = isFirst ? "prior" : "between";
  const m: Record<RetryStatKey, string> = {
    mean: `Mean ${t}`,
    median: `Median ${t}`,
    p90: `P90 ${t}`,
    p95: `P95 ${t}`,
    iqr: `IQR ${t}`,
    zero: `At 0 ${t}`,
  };
  return m[k];
}

function PathStatPill({
  label,
  value,
  className,
  help,
  labelShort,
}: {
  label: string;
  value: ReactNode;
  className: string;
  help: string;
  /** Aria/verbose label, e.g. "Mean of prior call attempts" */
  labelShort?: string;
}) {
  const a11y = labelShort ?? label;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-sm",
        className,
      )}
    >
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="font-semibold tabular-nums text-foreground">
        {value ?? "—"}
      </span>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className={cn(
              "shrink-0 rounded-full p-0.5 text-muted-foreground/70",
              "outline-none hover:bg-foreground/5 hover:text-foreground/80",
              "focus-visible:ring-2 focus-visible:ring-violet-500/40",
            )}
            aria-label={`${a11y} — more info`}
          >
            <HelpCircle className="h-3.5 w-3.5" strokeWidth={2.25} />
          </button>
        </TooltipTrigger>
        <TooltipContent
          className="max-w-[min(20rem,92vw)] space-y-1.5 p-3 text-left text-sm leading-relaxed"
          side="top"
        >
          {help}
        </TooltipContent>
      </Tooltip>
    </span>
  );
}

type PathQueryLists = { fromStatuses: string[]; toStatuses: string[] };

const TOKEN_FAIL = "fail:";
const TOKEN_PA = "pa:";

function parsePathToken(raw: string): { kind: "fail" | "pa"; value: string } | null {
  const t = raw.trim();
  const lower = t.toLowerCase();
  if (lower.startsWith(TOKEN_FAIL)) {
    return { kind: "fail", value: t.slice(TOKEN_FAIL.length).trim() };
  }
  if (lower.startsWith(TOKEN_PA)) {
    return { kind: "pa", value: t.slice(TOKEN_PA.length).trim() };
  }
  return null;
}

/** Sorted unique API tokens (``fail:`` / ``pa:``) for query params. */
function pathTokenSetsToApiLists(
  fromTokens: Set<string>,
  toTokens: Set<string>,
): PathQueryLists {
  const pick = (s: Set<string>) =>
    [...new Set([...s].filter((t) => parsePathToken(t)))].sort((a, b) =>
      a.localeCompare(b),
    );
  return { fromStatuses: pick(fromTokens), toStatuses: pick(toTokens) };
}

function formatTokenLabel(token: string): string {
  const p = parsePathToken(token);
  if (!p) return token;
  const label = formatEnumLabel(p.value);
  return p.kind === "fail" ? `Fail: ${label}` : `PA: ${label}`;
}

/** Chip label only (no Fail:/PA: prefix) — grouping headers carry the category. */
function formatTokenValueShort(token: string): string {
  const p = parsePathToken(token);
  if (!p) return token;
  return formatEnumLabel(p.value);
}

function PathStatusMultiField({
  label,
  hint,
  tokens,
  onTokensChange,
  failReasons,
  paStatuses,
  accent,
}: {
  label: string;
  hint: string;
  tokens: Set<string>;
  onTokensChange: Dispatch<SetStateAction<Set<string>>>;
  failReasons: string[];
  paStatuses: string[];
  accent: "from" | "to";
}) {
  const ring =
    accent === "from"
      ? "ring-1 ring-amber-500/20 border-amber-500/35"
      : "ring-1 ring-emerald-500/20 border-emerald-500/35";

  const toggle = useCallback(
    (token: string) => {
      onTokensChange((prev) => {
        const n = new Set(prev);
        if (n.has(token)) n.delete(token);
        else n.add(token);
        return n;
      });
    },
    [onTokensChange],
  );

  const sortedTokens = useMemo(
    () => Array.from(tokens).sort((a, b) => a.localeCompare(b)),
    [tokens],
  );

  const { failTokens, paTokens } = useMemo(() => {
    const fail: string[] = [];
    const pa: string[] = [];
    for (const t of sortedTokens) {
      const p = parsePathToken(t);
      if (p?.kind === "fail") fail.push(t);
      else if (p?.kind === "pa") pa.push(t);
    }
    return { failTokens: fail, paTokens: pa };
  }, [sortedTokens]);

  const chipClass =
    "h-6 max-w-full gap-0.5 py-0 pr-0.5 pl-1.5 text-[10px] font-normal leading-none";

  return (
    <div
      className={cn(
        "min-w-0 space-y-2 rounded-lg border border-border bg-card/50 p-3 shadow-sm",
        ring,
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium leading-tight text-foreground">{label}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>
        </div>
        {sortedTokens.length > 0 ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 shrink-0 text-xs text-muted-foreground"
            onClick={() => onTokensChange(new Set())}
          >
            Clear all
          </Button>
        ) : null}
      </div>

      {sortedTokens.length === 0 ? (
        <div className="flex min-h-[2.25rem] items-center">
          <span className="text-xs italic text-muted-foreground">
            {accent === "from"
              ? "Any prior failed call (all fail reasons)"
              : "Any successful PA (all PA outcomes)"}
          </span>
        </div>
      ) : (
        <div className="space-y-2.5">
          {failTokens.length > 0 ? (
            <div
              className={cn(
                "rounded-md border border-amber-500/25 bg-amber-500/[0.07] p-2 dark:bg-amber-950/25",
                "min-h-0",
              )}
            >
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900/90 dark:text-amber-100/90">
                Failed call — fail reason
                <span className="ml-1.5 font-normal text-amber-800/70 dark:text-amber-200/70">
                  ({failTokens.length})
                </span>
              </p>
              <div className="flex max-h-[7.5rem] flex-wrap gap-1 overflow-y-auto overscroll-y-contain pr-0.5">
                {failTokens.map((token) => (
                  <Badge
                    key={token}
                    variant="secondary"
                    className={cn(chipClass, "border-amber-500/20 bg-amber-50/90 dark:bg-amber-950/40")}
                  >
                    <span className="min-w-0 max-w-[10rem] truncate" title={formatTokenLabel(token)}>
                      {formatTokenValueShort(token)}
                    </span>
                    <button
                      type="button"
                      onClick={() => toggle(token)}
                      className="rounded-sm p-0.5 text-muted-foreground hover:bg-black/5 hover:text-foreground dark:hover:bg-white/10"
                      aria-label={`Remove fail reason ${formatTokenValueShort(token)}`}
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          ) : null}
          {paTokens.length > 0 ? (
            <div
              className={cn(
                "rounded-md border border-emerald-500/25 bg-emerald-500/[0.07] p-2 dark:bg-emerald-950/25",
                "min-h-0",
              )}
            >
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-900/90 dark:text-emerald-100/90">
                PA obtained — status
                <span className="ml-1.5 font-normal text-emerald-800/70 dark:text-emerald-200/70">
                  ({paTokens.length})
                </span>
              </p>
              <div className="flex max-h-[7.5rem] flex-wrap gap-1 overflow-y-auto overscroll-y-contain pr-0.5">
                {paTokens.map((token) => (
                  <Badge
                    key={token}
                    variant="secondary"
                    className={cn(
                      chipClass,
                      "border-emerald-500/20 bg-emerald-50/90 dark:bg-emerald-950/40",
                    )}
                  >
                    <span className="min-w-0 max-w-[10rem] truncate" title={formatTokenLabel(token)}>
                      {formatTokenValueShort(token)}
                    </span>
                    <button
                      type="button"
                      onClick={() => toggle(token)}
                      className="rounded-sm p-0.5 text-muted-foreground hover:bg-black/5 hover:text-foreground dark:hover:bg-white/10"
                      aria-label={`Remove PA status ${formatTokenValueShort(token)}`}
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-1.5">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 text-xs"
              aria-label={`Add ${label}`}
            >
              Add status
              <ChevronDown className="h-3.5 w-3.5 opacity-70" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="max-h-72 w-[min(100vw-2rem,20rem)] overflow-y-auto p-0"
            align="start"
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
              Failed call — fail reason
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="my-0" />
            {failReasons.map((fr) => {
              const token = `${TOKEN_FAIL}${fr}`;
              const sel = tokens.has(token);
              return (
                <DropdownMenuItem
                  key={token}
                  className="gap-2 text-sm"
                  onSelect={(e) => {
                    e.preventDefault();
                    toggle(token);
                  }}
                >
                  <span className="flex w-4 shrink-0 justify-center text-muted-foreground">
                    {sel ? <Check className="h-3.5 w-3.5" strokeWidth={2.5} /> : null}
                  </span>
                  <span className="min-w-0 break-words">{formatEnumLabel(fr)}</span>
                </DropdownMenuItem>
              );
            })}
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
              PA obtained — status
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="my-0" />
            {paStatuses.map((ps) => {
              const token = `${TOKEN_PA}${ps}`;
              const sel = tokens.has(token);
              return (
                <DropdownMenuItem
                  key={token}
                  className="gap-2 text-sm"
                  onSelect={(e) => {
                    e.preventDefault();
                    toggle(token);
                  }}
                >
                  <span className="flex w-4 shrink-0 justify-center text-muted-foreground">
                    {sel ? <Check className="h-3.5 w-3.5" strokeWidth={2.5} /> : null}
                  </span>
                  <span className="min-w-0 break-words">{formatEnumLabel(ps)}</span>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

const CHART_BAR_FILLS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "#8b5cf6",
  "hsl(var(--chart-3))",
  "#3b82f6",
  "#a855f7",
];

export function TransitionRetryChart({
  statsQuery: _statsQuery,
  refetchKey: _refetchKey,
  onLoadingChange,
  bundleTransition,
  bundleLoading,
  onPathFilterParamsChange,
  onBundleRefresh,
}: Props) {
  const { enums, loading: enumLoading, error: enumError } = usePaStructuredEnums();
  const [fromTokenSet, setFromTokenSet] = useState<Set<string>>(() => new Set());
  const [toTokenSet, setToTokenSet] = useState<Set<string>>(() => new Set());

  const bundlePath = useMemo(
    () => pathTokenSetsToApiLists(fromTokenSet, toTokenSet),
    [fromTokenSet, toTokenSet],
  );

  useEffect(() => {
    if (!enums) return;
    onPathFilterParamsChange(bundlePath.fromStatuses, bundlePath.toStatuses);
  }, [enums, bundlePath, onPathFilterParamsChange]);

  const data = bundleTransition;
  const loading = bundleLoading;
  const error: string | null = null;
  const refresh = onBundleRefresh;

  useEffect(() => {
    onLoadingChange?.(enumLoading || loading);
  }, [enumLoading, loading, onLoadingChange]);

  const chartRows = useMemo(() => {
    const b = data?.retries_breakdown;
    if (!b || Object.keys(b).length === 0) {
      return [];
    }
    return Object.keys(b)
      .map((k) => Number(k))
      .filter((n) => !Number.isNaN(n))
      .sort((a, b) => a - b)
      .map((n) => ({
        retrySteps: n,
        count: b[String(n)] ?? 0,
      }));
  }, [data]);

  const isInitialLoad = enumLoading || (loading && !data);
  const isRefetching = !enumLoading && loading && Boolean(data);
  const err = enumError ?? error;
  const avg = data?.average_retries;
  const med = data?.median_retries;
  const p90 = data?.p90_retries;
  const p95 = data?.p95_retries;
  const iqr = data?.iqr_retries;
  const shareZero = data?.share_zero_retries;
  const isFirstSuccessMetric = data?.path_metric === "first_success";

  return (
    <section
      className="rounded-xl border border-border bg-card shadow-sm"
      aria-label="Transition retry distribution"
    >
      <div
        className="border-b border-violet-500/15 bg-gradient-to-br from-violet-500/[0.09] via-card to-cyan-500/[0.07] px-4 py-3 sm:px-5 sm:py-4"
      >
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-violet-600/90 dark:text-violet-300/90">
          Path analytics
        </p>
        <h2 className="mt-1.5 text-lg font-semibold leading-tight tracking-tight text-foreground sm:text-xl">
          <span className="text-amber-600/95 dark:text-amber-400">From</span>
          <span className="mx-1 text-muted-foreground">→</span>
          <span className="text-emerald-600/95 dark:text-emerald-400">To</span>
        </h2>
        <p className="mt-1 text-xs text-muted-foreground max-w-2xl">
          With nothing selected, each side behaves like <span className="font-medium">Clear all</span> on
          that side: <span className="font-medium">From</span> is any prior failed call (all fail reasons) and{" "}
          <span className="font-medium">To</span> is any successful PA (all PA outcomes)—the first-success
          overview. Add chips to narrow either side.
        </p>
        {data?.capped ? (
          <p className="mt-2 text-xs text-amber-700/90 dark:text-amber-400/90">
            No case filters: using the {data.cap} most recent cases. Add filters
            to scope a different set.
          </p>
        ) : null}
        {isFirstSuccessMetric ? (
          <p className="mt-2 text-xs text-muted-foreground">
            With both sides empty (initial load), each bar is the number of call attempts{" "}
            <span className="font-medium">before</span> the first that obtained a PA in range (0 = success
            on the first call).
          </p>
        ) : null}
      </div>

      {enums && !enumLoading ? (
        <div className="space-y-3 border-b border-border/80 bg-muted/15 px-4 py-4 sm:px-5 sm:py-5">
          <div className="grid gap-5 sm:grid-cols-2 sm:gap-6">
            <PathStatusMultiField
              label="From status"
              hint="Nothing selected = any fail reason (same as Clear all). Add chips to allow only those rows on the From side."
              tokens={fromTokenSet}
              onTokensChange={setFromTokenSet}
              failReasons={enums.fail_reasons}
              paStatuses={enums.pa_statuses}
              accent="from"
            />
            <PathStatusMultiField
              label="To status"
              hint="Nothing selected = any successful PA (same as Clear all). Add chips to limit which outcomes end the path."
              tokens={toTokenSet}
              onTokensChange={setToTokenSet}
              failReasons={enums.fail_reasons}
              paStatuses={enums.pa_statuses}
              accent="to"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            <span className="font-medium">Clear all</span> matches the initial empty state for that side
            (unrestricted on that axis). Leave both sides empty for the first-success overview.
          </p>
        </div>
      ) : null}

      {err ? (
        <p className="px-4 py-2 text-sm text-destructive sm:px-5" role="alert">
          {err}
        </p>
      ) : null}

      <div className="space-y-3 px-4 pb-5 sm:px-5 sm:pb-6">
        <TooltipProvider delayDuration={250}>
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Path summary
              </h3>
              <div className="flex items-center gap-2">
                {isRefetching ? (
                  <span className="text-xs text-muted-foreground" aria-live="polite">
                    Updating…
                  </span>
                ) : null}
                <button
                  type="button"
                  className="text-xs font-medium text-violet-600 hover:underline dark:text-violet-400"
                  onClick={() => void refresh()}
                >
                  Refresh
                </button>
              </div>
            </div>

            <div className="divide-y overflow-hidden rounded-xl border border-border/80 bg-muted/5">
              <div className="p-3 sm:p-3.5">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Sample & coverage
                </p>
                <div className="mt-2.5 flex flex-wrap gap-2">
                  <PathStatPill
                    label="Matching"
                    value={data?.matched_case_count}
                    help={PATH_STAT_HELP.matching}
                    className="bg-violet-500/10 text-violet-900 dark:text-violet-200 border-violet-500/20"
                    labelShort="Matching path cases"
                  />
                  <PathStatPill
                    label="Documents"
                    value={data?.scanned_case_count}
                    help={PATH_STAT_HELP.documents}
                    className="bg-slate-500/8 text-foreground border-border"
                    labelShort="Firestore document rows"
                  />
                  <PathStatPill
                    label="Logical"
                    value={data?.grouped_case_count ?? data?.scanned_case_count}
                    help={PATH_STAT_HELP.logical}
                    className="bg-slate-500/8 text-foreground border-border"
                    labelShort="Logical cases after merge"
                  />
                </div>
              </div>

              <div
                className={cn(
                  "grid min-w-0 grid-cols-1 divide-y divide-border/80",
                  "md:grid-cols-2 md:divide-x md:divide-y-0 md:divide-border/80",
                )}
              >
                <div className="p-3 sm:p-3.5">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Typical & spread
                  </p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground/90">
                    Center, variability, and share with no extra calls (same per-case value as
                    the chart).
                  </p>
                  <div className="mt-2.5 flex flex-wrap gap-2">
                    <PathStatPill
                      label={pathStatLabel(isFirstSuccessMetric, "mean")}
                      value={formatFixed2(avg)}
                      help={helpForRetryStat("mean", isFirstSuccessMetric)}
                      className="bg-cyan-500/10 text-cyan-900 dark:text-cyan-200 border-cyan-500/20"
                    />
                    <PathStatPill
                      label={pathStatLabel(isFirstSuccessMetric, "median")}
                      value={formatFixed2(med)}
                      help={helpForRetryStat("median", isFirstSuccessMetric)}
                      className="bg-teal-500/10 text-teal-900 dark:text-teal-200 border-teal-500/20"
                    />
                    <PathStatPill
                      label={pathStatLabel(isFirstSuccessMetric, "iqr")}
                      value={formatFixed2(iqr)}
                      help={helpForRetryStat("iqr", isFirstSuccessMetric)}
                      className="bg-indigo-500/10 text-indigo-900 dark:text-indigo-200 border-indigo-500/20"
                    />
                    <PathStatPill
                      label={pathStatLabel(isFirstSuccessMetric, "zero")}
                      value={formatSharePercent(shareZero)}
                      help={helpForRetryStat("zero", isFirstSuccessMetric)}
                      className="bg-emerald-500/10 text-emerald-900 dark:text-emerald-200 border-emerald-500/20"
                    />
                  </div>
                </div>

                <div className="p-3 sm:p-3.5">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Upper percentiles
                  </p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground/90">
                    Heavier paths—useful for capacity and tail planning.
                  </p>
                  <div className="mt-2.5 flex flex-wrap gap-2">
                    <PathStatPill
                      label={pathStatLabel(isFirstSuccessMetric, "p90")}
                      value={formatFixed2(p90)}
                      help={helpForRetryStat("p90", isFirstSuccessMetric)}
                      className="bg-amber-500/10 text-amber-900 dark:text-amber-200 border-amber-500/20"
                    />
                    <PathStatPill
                      label={pathStatLabel(isFirstSuccessMetric, "p95")}
                      value={formatFixed2(p95)}
                      help={helpForRetryStat("p95", isFirstSuccessMetric)}
                      className="bg-orange-500/10 text-orange-900 dark:text-orange-200 border-orange-500/20"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TooltipProvider>

        {isInitialLoad && !data ? (
          <p className="text-sm text-muted-foreground py-10 text-center">Loading…</p>
        ) : chartRows.length === 0 && !loading && !enumLoading ? (
          <p className="text-sm text-muted-foreground py-10 text-center max-w-md mx-auto">
            No cases matched. Try different From/To sets, clear both sides for the overview, or
            broaden the dashboard case filters.
          </p>
        ) : (
          <div
            className={cn(
              "relative w-full min-w-0 pl-0 pr-1 sm:pr-0",
              isRefetching && "opacity-60",
            )}
          >
            {isRefetching ? (
              <div
                className="pointer-events-none absolute inset-0 z-10 flex items-start justify-end pt-2 pr-2"
                aria-live="polite"
              >
                <span className="rounded-md border border-border bg-background/90 px-2 py-1 text-xs text-muted-foreground shadow-sm backdrop-blur">
                  Loading…
                </span>
              </div>
            ) : null}
            <div className="h-80 w-full min-h-[18rem] rounded-lg border border-violet-500/10 bg-gradient-to-b from-violet-500/[0.04] to-transparent px-1 pt-2 pb-3 sm:px-2 sm:pb-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartRows}
                margin={{ top: 16, right: 10, left: 0, bottom: 52 }}
                barCategoryGap="12%"
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="hsl(var(--chart-grid) / 0.45)"
                  vertical={false}
                />
                <XAxis
                  dataKey="retrySteps"
                  tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                  tickLine={false}
                  axisLine={{ stroke: "hsl(var(--border))" }}
                  className="text-xs"
                  label={{
                    value: isFirstSuccessMetric
                      ? "Prior call attempts before first successful PA (0 = first call)"
                      : "Calls between paired from and to rows",
                    position: "insideBottom",
                    offset: isFirstSuccessMetric ? -4 : -2,
                    fill: "hsl(var(--muted-foreground))",
                    fontSize: 11,
                  }}
                />
                <YAxis
                  className="text-xs"
                  allowDecimals={false}
                  width={40}
                  tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  label={{
                    value: "Cases",
                    angle: -90,
                    position: "insideLeft",
                    fill: "hsl(var(--muted-foreground))",
                    fontSize: 11,
                  }}
                />
                <RechartsTooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.[0]) return null;
                    const p = payload[0].payload as {
                      retrySteps: number;
                      count: number;
                    };
                    return (
                      <div
                        className={cn(
                          "rounded-lg border border-violet-500/20 bg-popover/95 px-3 py-2 text-sm shadow-lg backdrop-blur-sm",
                        )}
                      >
                        <p className="font-medium text-foreground">
                          {isFirstSuccessMetric
                            ? `Prior calls before first success: ${p.retrySteps}`
                            : `Between calls: ${p.retrySteps}`}
                        </p>
                        <p className="text-muted-foreground">Cases: {p.count}</p>
                      </div>
                    );
                  }}
                />
                <Bar
                  dataKey="count"
                  name="Cases"
                  radius={[6, 6, 0, 0]}
                >
                  {chartRows.map((row, i) => (
                    <Cell
                      key={row.retrySteps}
                      fill={CHART_BAR_FILLS[i % CHART_BAR_FILLS.length]}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
